/* =============================================================================
   YOJO consent inset — R4 Wave 4.

   THE DEFECT. The cookie-consent banner is position:fixed at the bottom of the
   viewport and the page reserves NOTHING for it (measured: body padding-bottom
   0px at every width). So it sits on top of whatever occupies the last ~100px
   of the viewport, and a control underneath is not merely hard to see — it is
   not hit-testable. Measured on the deployed preview at 1440x900: a quick-add
   button at y=795 returned `H2.h4 cookies-title` from
   document.elementFromPoint at its own centre.

   Banner height is not a constant, so it cannot be hard-coded:
       320 -> 118px    390 -> 101px    430 -> 101px    768 -> 114px
      1280 ->  94px   1440 ->  94px   1920 ->  94px

   WHY THIS IS JAVASCRIPT AND NOT A `:has()` RULE. The obvious CSS fix is
   `body:has(modal-box.popup--cookies.active) { padding-bottom: … }`. It is
   wrong here: the `.active` class does NOT clear when the banner is dismissed
   (measured — after activating its dismiss control the element still carries
   `active` and still has a client rect). Keying the reserve on that class would
   leave a permanent empty strip at the foot of the page for the rest of the
   session, which is precisely the "do not create permanent blank space after
   dismissal" failure. Reserving the MEASURED height of an element that is
   actually rendered is self-correcting: when the banner truly goes, the height
   goes to 0 and the reserve disappears with it.

   WHAT IT DOES. Publishes the banner's real height as `--yojo-consent-h` on
   <html>; assets/yojo-design-system.css spends it as page-bottom padding. The
   banner keeps its own behaviour — consent is neither bypassed, hidden, nor
   re-styled, and no individual control is nudged upwards. The page simply
   becomes tall enough that nothing has to live underneath it.

   Lifecycle matches the Outcome B contract used by yojo-motion.js: one
   AbortController, idempotent init, real destroy, and observers disconnected.
   ========================================================================== */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.YojoConsentInset) return; // idempotent across section reloads

  /* 2026-08-06: Shopify's Customer Privacy banner (#shopify-pc__banner) is the
     single consent owner now — the modal-box app banner this script was written
     against no longer renders, so the old selector matched nothing and the
     property was never published. Shopify's own stylesheet pads the BODY for
     its banner, so in-flow content already clears it; what the measured height
     is still needed for is the fixed-position a11y-widget FAB
     (#mic-access-tool-general-button), which body padding cannot move — see
     the bottom-offset rule in yojo-design-system.css. The old selectors are
     kept behind it as a harmless fallback. */
  var SELECTOR = '#shopify-pc__banner, modal-box.popup--cookies, modal-box[class*="cookies"]';
  var PROP = '--yojo-consent-h';
  var ac = new AbortController();
  var ro = null;
  var mo = null;
  var raf = 0;

  function visibleHeight(el) {
    if (!el || !el.getClientRects().length) return 0;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return 0;
    // Only a viewport-anchored overlay can occlude page content. An in-flow
    // banner is already part of the layout and must not be double-counted.
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return 0;
    var r = el.getBoundingClientRect();
    if (r.height <= 0) return 0;
    var vh = window.innerHeight || 0;
    if (!vh) return 0;

    /* Bottom-anchored only — a banner pinned to the top does not trap the end
       of the document. "Anchored" cannot mean flush, though: measured computed
       styles differ by width —
           390   inset 742.6px 0 0        bottom: 0     full-bleed
          1440   inset 789.8px 0 16px     bottom: 16px  max-width 960, centred
       so an exact-bottom test (the first attempt used a 4px tolerance) silently
       rejected every desktop width and reserved nothing there. Allow the banner
       to float clear of the edge. */
    if (r.bottom < vh * 0.6) return 0;

    /* Reserve from the banner's TOP to the bottom of the viewport, not merely
       its height: the gap underneath it is just as unusable as the banner. */
    return Math.ceil(vh - r.top);
  }

  /* 2026-08-06 (owner-directed): Shopify's privacy banner renders its DEFAULT
     ENGLISH strings on this Hebrew-primary store. The data-side fix is
     impossible: translationsRegister refuses the shop's primary locale
     ("Locale cannot be the same as the shop's primary locale") and the Admin
     UI only customizes appearance/position, not text. So the theme relabels
     the rendered strings with the owner's approved Hebrew copy — an
     EXACT-STRING map over text nodes inside the banner and its preferences
     dialog only. Consent semantics, buttons and events are untouched; a
     string that doesn't exactly match a known Shopify default is never
     modified. Runs from the same MutationObserver as the height measurement
     (microtask — before paint, so no English flash), idempotent by
     construction (Hebrew no longer matches the English keys). */
  var HE_STRINGS = {
    'Cookie consent': 'אנו מכבדים את פרטיותך!',
    /* Key is TRIMMED (the live text node ends with a space before the policy
       link; localize() compares trimmed values and re-inserts into the raw
       node, so surrounding whitespace survives). */
    'We and our partners, including Shopify, use cookies and other technologies to personalize your experience, show you ads, and perform analytics, and we will not use cookies or other technologies for these purposes unless you accept them. Learn more in our':
      'אנו משתמשים בעוגיות כדי לשפר את חוויית הגלישה שלך. באפשרותך לבחור אילו עוגיות לאשר.',
    'Privacy Policy': 'מדיניות עוגיות',
    'Manage preferences': 'ניהול העדפות',
    'Accept': 'קבל הכל',
    'Decline': 'סירוב',
    'Cookie and privacy preferences': 'העדפות עוגיות ופרטיות',
    'You control your data': 'השליטה במידע בידיים שלך',
    'Learn more about the cookies we use, and choose which cookies to allow.': 'למד על העוגיות שבשימוש באתר ובחר אילו לאשר.',
    'Accept all': 'קבל הכל',
    'Decline all': 'סירוב לכל',
    'Save my choices': 'שמירת הבחירות שלי',
    'If you accept all cookies': 'אם תאשר את כל העוגיות',
    'Our site may be improved based on your interests': 'האתר יוכל להשתפר בהתאם לתחומי העניין שלך',
    'Your preferences may be stored for when you return': 'ההעדפות שלך יישמרו לביקור הבא',
    "You may see advertising that's tailored to you": 'ייתכן שתוצג לך פרסומת מותאמת אישית',
    'Required': 'חובה',
    'These cookies are necessary for the site to function properly, including capabilities like logging in and adding items to the cart.':
      'עוגיות אלה נחוצות לתפקוד תקין של האתר, כולל התחברות והוספת מוצרים לעגלה.',
    'Analytics': 'אנליטיקה',
    'These cookies help us understand how you interact with the site. We use this data to identify areas to improve.':
      'עוגיות אלה עוזרות לנו להבין איך משתמשים באתר, כדי לזהות מה אפשר לשפר.',
    'Personalization': 'התאמה אישית',
    'These cookies store details about your actions to personalize your next visit to the website.':
      'עוגיות אלה שומרות מידע על הפעולות שלך כדי להתאים אישית את הביקור הבא באתר.',
    'Marketing': 'שיווק',
    'These cookies are used by us and our partners, including Shopify, to optimize marketing communications and show you ads on other websites.':
      'עוגיות אלה משמשות אותנו ואת השותפים שלנו, כולל Shopify, לשיפור התקשורת השיווקית ולהצגת פרסומות באתרים אחרים.'
  };

  function localize() {
    if ((document.documentElement.lang || '').indexOf('he') !== 0) return;
    var roots = document.querySelectorAll('#shopify-pc__banner, #shopify-pc__prefs');
    for (var i = 0; i < roots.length; i++) {
      var walker = document.createTreeWalker(roots[i], NodeFilter.SHOW_TEXT);
      var n;
      while ((n = walker.nextNode())) {
        var raw = n.nodeValue;
        var t = raw.trim();
        if (t && Object.prototype.hasOwnProperty.call(HE_STRINGS, t)) {
          n.nodeValue = raw.replace(t, HE_STRINGS[t]);
        }
      }
    }
  }

  function apply() {
    raf = 0;
    localize();
    var el = document.querySelector(SELECTOR);
    var h = visibleHeight(el);
    var root = document.documentElement;
    if (h > 0) {
      root.style.setProperty(PROP, h + 'px');
      if (ro && el) { try { ro.observe(el); } catch (e) { /* already observed */ } }
    } else {
      // Removing the property (rather than setting 0px) lets the stylesheet's
      // own fallback win, so nothing is reserved once the banner is gone.
      root.style.removeProperty(PROP);
    }

    /* 2026-08-06: --yojo-preview-bar-h was measured only by the PDP sticky-ATC
       snippet (Recovery #18), but the consent banner renders on EVERY route
       and Shopify's preview toolbar (#PBarNextFrameWrapper, a native-popover
       top-layer iframe) sits on top of its buttons at the viewport bottom on
       preview_theme_id URLs — measured at 1440x900: the Accept button's center
       hit-tested to #PBarNextFrame. Publish the same property globally from
       here (this script already observes the DOM); the PDP snippet setting the
       same value is harmless. Always 0px on the published live theme, where
       the toolbar does not exist. rect height (not offsetParent) per the
       Recovery #18 note: offsetParent is null for every fixed element. */
    var pbar = document.getElementById('PBarNextFrameWrapper');
    var ph = pbar ? Math.ceil(pbar.getBoundingClientRect().height) : 0;
    if (ph > 0) {
      root.style.setProperty('--yojo-preview-bar-h', ph + 'px');
    }
  }

  function schedule() {
    if (raf) return;
    raf = window.requestAnimationFrame(apply);
  }

  function init() {
    if (typeof ResizeObserver === 'function' && !ro) ro = new ResizeObserver(schedule);
    if (typeof MutationObserver === 'function' && !mo) {
      mo = new MutationObserver(schedule);
      mo.observe(document.body, {
        childList: true, subtree: true,
        attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      });
    }
    window.addEventListener('resize', schedule, { passive: true, signal: ac.signal });
    window.addEventListener('orientationchange', schedule, { passive: true, signal: ac.signal });
    document.addEventListener('shopify:section:load', schedule, { signal: ac.signal });
    schedule();
  }

  function destroy() {
    ac.abort();
    if (ro) { try { ro.disconnect(); } catch (e) {} ro = null; }
    if (mo) { try { mo.disconnect(); } catch (e) {} mo = null; }
    if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
    document.documentElement.style.removeProperty(PROP);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.YojoConsentInset = { apply: schedule, destroy: destroy };
})();
